import {
    computed,
    defineComponent,
    onMounted,
    PropType,
    ref,
} from "vue";

import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../utils.js";
import ImageFallback from "../components/imageFallback.js";
import LogSearchEditor from "./editor/LogSearchEditor.js";
import {
    LogSearchExamples,
} from "./editor/schema.js";
import { getLogSearchDiagnostics } from "./editor/diagnostics.js";
import {
    createExpressionBookmarkId,
    ExpressionBookmark,
    loadExpressionBookmarks,
    saveExpressionBookmarks,
} from "./bookmarks/bookmarkStorage.js";
import {
    ExpressionPlaceholder,
    parseExpressionPlaceholders,
    resolveExpressionPlaceholders,
} from "./placeholders.js";
import { renderExpressionPreview } from "./preview.js";

type PlaceholderRoleOption = {
    id: string;
    name: string;
};

type PlaceholderChannelOption = {
    id: string;
    name: string;
};

type PlaceholderUserOption = Parameters<typeof getMemberDisplayName>[0] & {
    id: string;
};

export const ExpressionFilter = defineComponent({
    props: {
        expression: {
            type: String,
            default: "",
        },

        onsave: {
            type: Function as PropType<(expression: string) => void>,
            required: true,
        },
    },

    setup(props) {
        const value = ref(props.expression);
        const bookmarkName = ref("");
        const selectedBookmarkId = ref<string | null>(null);
        const bookmarks = ref<ExpressionBookmark[]>(loadExpressionBookmarks());
        const placeholderValues = ref<Record<string, string>>({});
        const roleOptions = ref<PlaceholderRoleOption[]>([]);
        const channelOptions = ref<PlaceholderChannelOption[]>([]);
        const userOptions = ref<Record<string, PlaceholderUserOption[]>>({});
        const userQueries = ref<Record<string, string>>({});
        const selectedUsers = ref<Record<string, PlaceholderUserOption | null>>({});
        const loadingOptions = ref<Record<string, boolean>>({});
        const bookmarksOpen = ref(false);
        const bookmarkSearch = ref("");
        const examplesOpen = ref(false);
        const exampleSearch = ref("");
        const placeholders = computed(() => parseExpressionPlaceholders(value.value));
        const resolvedExpression = computed(() => resolveExpressionPlaceholders(value.value, placeholderValues.value));
        const diagnostics = computed(() => getLogSearchDiagnostics(value.value));
        const filteredExamples = computed(() => {
            const query = exampleSearch.value.trim().toLowerCase();

            if (!query)
                return LogSearchExamples;

            return LogSearchExamples.filter((example) => (
                example.name.toLowerCase().includes(query)
                || example.description.toLowerCase().includes(query)
                || example.expression.toLowerCase().includes(query)
            ));
        });
        const filteredBookmarks = computed(() => {
            const query = bookmarkSearch.value.trim().toLowerCase();

            if (!query)
                return bookmarks.value;

            return bookmarks.value.filter((bookmark) => (
                bookmark.name.toLowerCase().includes(query)
                || bookmark.expression.toLowerCase().includes(query)
            ));
        });

        async function ensureOptions(type: string) {
            if (loadingOptions.value[type])
                return;

            if (type === "role" && !roleOptions.value.length) {
                loadingOptions.value = Object.assign({}, loadingOptions.value, { role: true });
                roleOptions.value = await fetch("/api/roles").then((res) => res.json());
                loadingOptions.value = Object.assign({}, loadingOptions.value, { role: false });
            }

            if (type === "channel" && !channelOptions.value.length) {
                loadingOptions.value = Object.assign({}, loadingOptions.value, { channel: true });
                channelOptions.value = await fetch("/api/channels").then((res) => res.json());
                loadingOptions.value = Object.assign({}, loadingOptions.value, { channel: false });
            }
        }

        async function searchUsers(placeholder: ExpressionPlaceholder, query: string) {
            userQueries.value = Object.assign({}, userQueries.value, {
                [placeholder.key]: query,
            });

            if (!query.trim()) {
                userOptions.value = Object.assign({}, userOptions.value, {
                    [placeholder.key]: [],
                });
                return;
            }

            const loadingKey = `user:${placeholder.key}`;

            if (loadingOptions.value[loadingKey])
                return;

            try {
                loadingOptions.value = Object.assign({}, loadingOptions.value, { [loadingKey]: true });

                while (true) {
                    const currentQuery = userQueries.value[placeholder.key] ?? "";
                    const users = await fetch(`/api/search-users?q=${encodeURIComponent(currentQuery)}`)
                        .then((res) => res.status === 200 ? res.json() : []);

                    userOptions.value = Object.assign({}, userOptions.value, {
                        [placeholder.key]: users,
                    });

                    if (currentQuery === userQueries.value[placeholder.key])
                        return;
                }

            } finally {
                loadingOptions.value = Object.assign({}, loadingOptions.value, { [loadingKey]: false });
            }
        }

        onMounted(() => {
            for (const placeholder of placeholders.value)
                ensureOptions(placeholder.type);
        });

        function submit() {
            if (resolvedExpression.value.missing.length)
                return;

            props.onsave(resolvedExpression.value.resolved.trim());
        }

        function saveCurrentBookmark() {
            const expression = value.value.trim();
            const name = bookmarkName.value.trim();

            if (!expression)
                return;

            if (selectedBookmarkId.value) {
                bookmarks.value = bookmarks.value.map((bookmark) => (
                    bookmark.id === selectedBookmarkId.value
                        ? {
                            ...bookmark,
                            name        : name || bookmark.name,
                            expression,
                        }
                        : bookmark
                ));
                saveExpressionBookmarks(bookmarks.value);
                return;
            }

            const bookmark: ExpressionBookmark = {
                id          : createExpressionBookmarkId(),
                name        : name || `Expression ${bookmarks.value.length + 1}`,
                expression,
                createdAt   : Date.now(),
            };

            bookmarks.value = [ bookmark, ...bookmarks.value ].slice(0, 50);
            selectedBookmarkId.value = bookmark.id;
            bookmarkName.value = bookmark.name;
            saveExpressionBookmarks(bookmarks.value);
        }

        function selectBookmark(bookmark: ExpressionBookmark) {
            value.value = bookmark.expression;
            bookmarkName.value = bookmark.name;
            selectedBookmarkId.value = bookmark.id;
            placeholderValues.value = {};
            bookmarksOpen.value = false;
            bookmarkSearch.value = "";

            for (const placeholder of parseExpressionPlaceholders(bookmark.expression))
                ensureOptions(placeholder.type);
        }

        function deleteBookmark(bookmark: ExpressionBookmark) {
            bookmarks.value = bookmarks.value.filter((item) => item.id !== bookmark.id);

            if (selectedBookmarkId.value === bookmark.id) {
                selectedBookmarkId.value = null;
                bookmarkName.value = "";
            }

            saveExpressionBookmarks(bookmarks.value);
        }

        function deleteBookmarkFromList(bookmark: ExpressionBookmark, event: Event) {
            event.stopPropagation();
            deleteBookmark(bookmark);
        }

        function selectExample(expression: string) {
            value.value = expression;
            selectedBookmarkId.value = null;
            examplesOpen.value = false;
            exampleSearch.value = "";
        }

        function renderPlaceholderInput(placeholder: ExpressionPlaceholder) {
            const value = placeholderValues.value[placeholder.key] ?? "";

            function update(nextValue: string) {
                placeholderValues.value = Object.assign({}, placeholderValues.value, {
                    [placeholder.key]: nextValue,
                });
            }

            if (placeholder.type === "role")
                return (
                    <select value={value} onFocus={() => ensureOptions("role")} onChange={(event) => update((event.target as HTMLSelectElement).value)}>
                        <option value="">{loadingOptions.value.role ? "Chargement..." : placeholder.label}</option>
                        {roleOptions.value.map((role) => <option value={role.id}>{role.name}</option>)}
                    </select>
                );

            if (placeholder.type === "channel")
                return (
                    <select value={value} onFocus={() => ensureOptions("channel")} onChange={(event) => update((event.target as HTMLSelectElement).value)}>
                        <option value="">{loadingOptions.value.channel ? "Chargement..." : placeholder.label}</option>
                        {channelOptions.value.map((channel) => <option value={channel.id}>{channel.name}</option>)}
                    </select>
                );

            if (placeholder.type === "user") {
                const selectedUser = selectedUsers.value[placeholder.key];
                const options = userOptions.value[placeholder.key] ?? [];
                const loadingKey = `user:${placeholder.key}`;

                function selectUser(user: PlaceholderUserOption) {
                    selectedUsers.value = Object.assign({}, selectedUsers.value, {
                        [placeholder.key]: user,
                    });
                    userQueries.value = Object.assign({}, userQueries.value, {
                        [placeholder.key]: getMemberDisplayName(user),
                    });
                    update(user.id);
                    userOptions.value = Object.assign({}, userOptions.value, {
                        [placeholder.key]: [],
                    });
                }

                return (
                    <div class="query-placeholder-user">
                        <input
                            value={userQueries.value[placeholder.key] ?? (selectedUser ? getMemberDisplayName(selectedUser) : value)}
                            type="text"
                            placeholder={placeholder.label}
                            onInput={(event) => {
                                update("");
                                selectedUsers.value = Object.assign({}, selectedUsers.value, {
                                    [placeholder.key]: null,
                                });
                                searchUsers(placeholder, (event.target as HTMLInputElement).value);
                            }}
                            onFocus={(event) => searchUsers(placeholder, (event.target as HTMLInputElement).value)}
                        />
                        {loadingOptions.value[loadingKey] && <div class="query-placeholder-user-info">Recherche en cours...</div>}
                        {options.length > 0 && (
                            <div class="query-placeholder-user-options">
                                {options.map((user) => (
                                    <button type="button" onClick={() => selectUser(user)}>
                                        <ImageFallback class="query-placeholder-user-avatar" src={getMemberAvatar(user)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(user)} />
                                        <span>{getMemberDisplayName(user)}</span>
                                        <small>{getMemberTag(user)}</small>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <input
                    value={value}
                    type={placeholder.type === "number" ? "number" : "text"}
                    placeholder={placeholder.label}
                    onInput={(event) => update((event.target as HTMLInputElement).value)}
                />
            );
        }

        return () => (
            <div class="query-filter">
                <div class="query-bookmark-savebar">
                    <span>Bookmark courant</span>
                    <div class="query-bookmark-save">
                        <input
                            value={bookmarkName.value}
                            placeholder="Nom du bookmark"
                            onInput={(event) => (bookmarkName.value = (event.target as HTMLInputElement).value)}
                        />
                        <button type="button" onClick={saveCurrentBookmark}>
                            {selectedBookmarkId.value ? "Mettre à jour" : "Sauvegarder"}
                        </button>
                    </div>
                </div>

                <LogSearchEditor
                    value={value.value}
                    onupdate={(nextValue) => {
                        value.value = nextValue;
                        selectedBookmarkId.value = null;
                    }}
                    onsubmit={submit}
                    onexamples={() => (examplesOpen.value = true)}
                    onbookmarks={() => (bookmarksOpen.value = true)}
                />

                {diagnostics.value.length > 0 && (
                    <div class="query-editor-actions">
                        <div class="query-diagnostics-inline">
                            {diagnostics.value.map((diagnostic) => (
                                <span class={`query-diagnostic query-diagnostic-${diagnostic.severity}`}>
                                    {diagnostic.message}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {bookmarksOpen.value && (
                    <div class="query-example-overlay" onClick={() => (bookmarksOpen.value = false)}>
                        <div class="query-example-modal" onClick={(event) => event.stopPropagation()}>
                            <div class="query-example-header">
                                <div>
                                    <b>Bookmarks d'expressions</b>
                                    <span>Sélectionne une expression sauvegardée ou supprime celles inutiles.</span>
                                </div>
                                <button type="button" onClick={() => (bookmarksOpen.value = false)}>
                                    <span class="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <input
                                class="query-example-search"
                                value={bookmarkSearch.value}
                                placeholder="Rechercher un bookmark..."
                                autofocus
                                onInput={(event) => (bookmarkSearch.value = (event.target as HTMLInputElement).value)}
                            />
                            <div class="query-example-list">
                                {filteredBookmarks.value.map((bookmark) => (
                                    <button type="button" class="query-example-item query-bookmark-modal-item" onClick={() => selectBookmark(bookmark)}>
                                        <div class="query-bookmark-modal-title">
                                            <b>{bookmark.name}</b>
                                            <button type="button" class="query-bookmark-modal-delete" onClick={(event) => deleteBookmarkFromList(bookmark, event)}>
                                                <span class="material-symbols-outlined">delete</span>
                                            </button>
                                        </div>
                                        <code>{renderExpressionPreview(bookmark.expression)}</code>
                                    </button>
                                ))}
                                {!filteredBookmarks.value.length && (
                                    <div class="query-example-empty">
                                        {bookmarks.value.length ? "Aucun bookmark trouvé." : "Aucun bookmark sauvegardé."}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {examplesOpen.value && (
                    <div class="query-example-overlay" onClick={() => (examplesOpen.value = false)}>
                        <div class="query-example-modal" onClick={(event) => event.stopPropagation()}>
                            <div class="query-example-header">
                                <div>
                                    <b>Exemples d'expressions</b>
                                    <span>Sélectionne un modèle, puis ajuste les IDs/champs.</span>
                                </div>
                                <button type="button" onClick={() => (examplesOpen.value = false)}>
                                    <span class="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <input
                                class="query-example-search"
                                value={exampleSearch.value}
                                placeholder="Rechercher un exemple..."
                                autofocus
                                onInput={(event) => (exampleSearch.value = (event.target as HTMLInputElement).value)}
                            />
                            <div class="query-example-list">
                                {filteredExamples.value.map((example) => (
                                    <button type="button" class="query-example-item" onClick={() => selectExample(example.expression)}>
                                        <b>{example.name}</b>
                                        <small>{example.description}</small>
                                        <code>{renderExpressionPreview(example.expression)}</code>
                                    </button>
                                ))}
                                {!filteredExamples.value.length && (
                                    <div class="query-example-empty">Aucun exemple trouvé.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {placeholders.value.length > 0 && (
                    <div class="query-placeholder-panel">
                        <div class="query-placeholder-title">Paramètres du bookmark</div>
                        <div class="query-placeholder-list">
                            {placeholders.value.map((placeholder) => (
                                <label class="query-placeholder-field">
                                    <span>{placeholder.label}</span>
                                    <small>{placeholder.type}</small>
                                    {renderPlaceholderInput(placeholder)}
                                </label>
                            ))}
                        </div>
                        <div class="query-placeholder-preview">
                            <span>Expression exécutée</span>
                            <LogSearchEditor
                                value={resolvedExpression.value.resolved}
                                onupdate={() => undefined}
                                readonly
                                autoHeight
                                footer={false}
                            />
                        </div>
                    </div>
                )}

                <div class="log-filter-submit" onClick={submit}>
                    <span class="material-symbols-outlined">search</span>
                    {resolvedExpression.value.missing.length
                        ? "Renseigner les paramètres"
                        : "Rechercher avec l'expression"}
                </div>
            </div>
        );
    },
});
